import {PropsWithChildren} from "react";
import {createContext} from "../../../../../context/create-context";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {Button, Result} from "antd";
import {useSession, Session} from "../../../../../features/sessions";
import {GetSessionApiResponse} from "../../../../../react-query-hooks/session/types/edit-session";
import {useParams} from "react-router-dom";
import {sessionToLegacyAdapter} from "../../../editService/context/get-session/session-to-legacy.adapter";

type GetBundleState = {
  sessionData: GetSessionApiResponse;
  session: Session | undefined;
  isFetchingBundle: boolean;
  isLoadingBundle: boolean;
};

type GetBundleContextState = {
  state: GetBundleState;
};

const [useContext, ContextProvider] = createContext<GetBundleContextState>();

export const useGetBundleContext = useContext;

export const GetBundleProvider = ({children}: PropsWithChildren) => {
  const {id} = useParams();
  // Convert string id to number for useSession hook
  const bundleId = id ? parseInt(id, 10) : 0;
  const getBundleQuery = useSession(bundleId, !!id && bundleId > 0);

  if (getBundleQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (getBundleQuery.isError) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching bundle"
        extra={
          <Button type="primary" size="small" onClick={() => getBundleQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  // Adapt Session entity to legacy GetSessionApiResponse format for consumer compatibility
  // TODO: Remove adapter when consumers are updated to use Session entity directly
  const sessionData = getBundleQuery.data
    ? sessionToLegacyAdapter(getBundleQuery.data)
    : (undefined as unknown as GetSessionApiResponse);

  const state: GetBundleState = {
    sessionData,
    session: getBundleQuery.data,
    isFetchingBundle: getBundleQuery.isFetching,
    isLoadingBundle: getBundleQuery.isLoading,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
